import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { LANG, saveLang } from "./i18n";

// staff language follows the account; reload once if this browser shows the other one
const followLang = (u) => {
  if (u?.language && u.language !== LANG) { saveLang(u.language); window.location.reload(); }
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = signed-in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Skip on public pages where no token was ever issued — silences 401 noise.
    const hasToken = !!localStorage.getItem("access_token");
    const inPreviewFrame = window.self !== window.top; // theme preview iframe: never touch the staff session
    if (!hasToken || inPreviewFrame) {
      setUser(false);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      followLang(data);
      setUser(data);
      return data;
    } catch {
      // Token stale/invalid — clear it and treat as anonymous
      localStorage.removeItem("access_token");
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    if (data.mfa) return data;
    return completeLogin(data);
  };

  const completeLogin = (data) => {
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    followLang(data.user);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    localStorage.removeItem("access_token");
    setUser(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const { data } = await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    // Other sessions are ended server-side; keep this device signed in with the fresh token.
    if (data?.access_token) localStorage.setItem("access_token", data.access_token);
    await refresh();
  };

  const updateProfile = async (patch) => {
    const { data } = await api.post("/auth/update-profile", patch);
    setUser(data);
    return data;
  };

  const can = (module, action) => {
    if (!user || user === false) return false;
    if (user.role === "admin") return true;
    return !!user.permissions?.[module]?.[action];
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, refresh, login, completeLogin, logout, changePassword, updateProfile, can }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
